import { useState, useCallback } from "react";
import {
  createEvent,
  updateEvent,
  deleteEvent,
  duplicateEvent,
  type CreateEventInput,
  type UpdateEventInput,
} from "../services/eventService";
import type { DuplicateEventInput } from "@event-planner/shared";

export function useCreateEvent() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const create = useCallback(async (input: CreateEventInput, eventId?: string, creatorEmail?: string) => {
    setLoading(true);
    setError(null);
    try {
      const id = await createEvent(input, eventId, creatorEmail);
      return id;
    } catch (err) {
      const e = err instanceof Error ? err : new Error("Create event failed");
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return { create, loading, error };
}

export function useUpdateEvent() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const update = useCallback(
    async (eventId: string, input: UpdateEventInput) => {
      setLoading(true);
      setError(null);
      try {
        await updateEvent(eventId, input);
      } catch (err) {
        const e =
          err instanceof Error ? err : new Error("Update event failed");
        setError(e);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { update, loading, error };
}

export function useDeleteEvent() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const remove = useCallback(async (eventId: string) => {
    setLoading(true);
    setError(null);
    try {
      await deleteEvent(eventId);
    } catch (err) {
      const e = err instanceof Error ? err : new Error("Delete event failed");
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return { remove, loading, error };
}

export function useDuplicateEvent() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const duplicate = useCallback(
    async (input: DuplicateEventInput, creatorEmail?: string) => {
      setLoading(true);
      setError(null);
      try {
        return await duplicateEvent(input, creatorEmail);
      } catch (err) {
        const e =
          err instanceof Error ? err : new Error("Duplicate event failed");
        setError(e);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { duplicate, loading, error };
}
